import { Box, Menu, MenuProps, Stack } from '@mui/material';
import { MenuButton } from './MenuButton';
import { Button, IconButton, colors } from '@sweep-io/sweep-design';
import usePopoverMenu from '../../../../hooks/usePopover';
import { EnvMenuItems } from './EnvMenuItems';
import { useCallback, useState } from 'react';
import { ManageEnvDialog } from './ManageEnvDialog';
import { useSelector } from 'react-redux';
import { selectConnectedCrmOrgs, selectDefaultCreationEnvironment } from '../environmentsReducer';
import ConnectToSfDialog from '../../get-started/connect-org/ConnectToSfDialog';
import { Cloud } from '@sweep-io/sweep-design/dist/icons';
import { TruncatedTextTooltip } from '../../../common/TruncatedTextTooltip';
import WithConnectOrgPermission from '../../../common/WithConnectOrgPermission';

enum ConnectDialogType {
  REGULAR = 'REGULAR',
  FORCE_PRODUCTION = 'FORCE_PRODUCTION',
}

export const EnvSelector = (
  {
    variant = 'full',
    menuProps,
    allowDeleteLastEnv,
    onChangeEnvCallback,
    disabled,
    disableManageEnv,
    allowedEnvIds,
  }: {
    variant?: 'icon-button' | 'full' | 'text';
    menuProps?: Pick<MenuProps, 'anchorOrigin' | 'transformOrigin'>;
    allowDeleteLastEnv?: boolean;
    disabled?: boolean;
    onChangeEnvCallback?: () => void;
    disableManageEnv?: boolean;
    allowedEnvIds?: string[];
  } = { allowDeleteLastEnv: false },
) => {
  const { isPopoverOpen, closePopover, openPopover, anchorElement, anchorRef } = usePopoverMenu();
  const [openedConnectDialogType, setOpenedConnectDialogType] = useState<ConnectDialogType | null>(
    null,
  );
  const selectedEnv = useSelector(selectDefaultCreationEnvironment);
  const environments = useSelector(selectConnectedCrmOrgs);

  const [isEnvDialogOpen, setIsEnvDialogOpen] = useState(false);
  const closeEnvDialog = useCallback(() => setIsEnvDialogOpen(false), [setIsEnvDialogOpen]);

  const onConnectProdClick = () => {
    closePopover();
    setOpenedConnectDialogType(ConnectDialogType.FORCE_PRODUCTION);
  };

  const closeSimpleDialog = useCallback(() => {
    setOpenedConnectDialogType(null);
  }, []);

  const onManageEnvClick = useCallback(() => {
    closePopover();
    setIsEnvDialogOpen(true);
  }, [closePopover]);

  const onConnectToSfClick = useCallback(() => {
    closePopover();
    setOpenedConnectDialogType(ConnectDialogType.REGULAR);
  }, [closePopover]);

  const renderSimpleConnectDialog = () => {
    if (openedConnectDialogType) {
      return (
        <ConnectToSfDialog
          closeDialog={closeSimpleDialog}
          forceProduction={openedConnectDialogType === ConnectDialogType.FORCE_PRODUCTION}
          onFinish={closeSimpleDialog}
        />
      );
    }
  };

  const renderManageEnvDialog = () => {
    if (isEnvDialogOpen) {
      return (
        <ManageEnvDialog
          onClose={closeEnvDialog}
          allowDeleteLastEnv={Boolean(allowDeleteLastEnv)}
        />
      );
    }
  };

  const envName = selectedEnv?.name ?? '';

  const renderButton = () => {
    switch (variant) {
      case 'icon-button':
        return (
          <IconButton variant="flat" ref={anchorRef} onClick={openPopover} disabled={disabled}>
            <Cloud size="medium" />
          </IconButton>
        );

      case 'full':
        return (
          <MenuButton
            anchorRef={anchorRef}
            onClick={openPopover}
            pressed={isPopoverOpen}
            disabled={disabled}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                minWidth: 0,
                width: '175px',
              }}
            >
              <TruncatedTextTooltip variant={'body'} color={colors.grey[900]}>
                {envName}
              </TruncatedTextTooltip>
            </Box>
          </MenuButton>
        );
      case 'text':
        return (
          <Button
            onClick={openPopover}
            ref={anchorRef}
            variant="flat-grey"
            size="small"
            endIconName={isPopoverOpen ? 'ChevronUp' : 'ChevronDown'}
          >
            {envName}
          </Button>
        );
    }
  };

  const renderEmptyButton = () => (
    <Stack sx={{ '& > button': { whiteSpace: 'nowrap' } }}>
      <WithConnectOrgPermission>
        <Button size="small" onClick={onConnectToSfClick}>
          Connect to Salesforce
        </Button>
      </WithConnectOrgPermission>
    </Stack>
  );

  const onDoubleClick = useCallback(() => {
    if (selectedEnv) {
      navigator.clipboard.writeText(selectedEnv.id);
      console.log('Copied current salesforce org id to clipboard', selectedEnv.id);
    }
  }, [selectedEnv]);

  return (
    <>
      <Box onDoubleClick={onDoubleClick}>
        {environments.length ? renderButton() : renderEmptyButton()}
        <Menu
          id="user-menu"
          anchorEl={anchorElement}
          open={isPopoverOpen}
          onClose={() => closePopover()}
          MenuListProps={{
            'aria-labelledby': 'user-menu-button',
          }}
          slotProps={{
            paper: {
              sx: (theme) => ({
                marginTop: '0px',
                width: 'auto',
                boxShadow: theme.shadows[7],
              }),
            },
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          {...menuProps}
        >
          <EnvMenuItems
            onConnectProdClick={onConnectProdClick}
            onManageEnvClick={onManageEnvClick}
            close={closePopover}
            onChangeEnvCallback={onChangeEnvCallback}
            disableManageEnv={disableManageEnv}
            allowedEnvIds={allowedEnvIds}
          />
        </Menu>
      </Box>
      {renderSimpleConnectDialog()}
      {renderManageEnvDialog()}
    </>
  );
};
